import { useState, useEffect, useRef } from 'react';
import { useBanAutocomplete } from '../../hooks/useBanAutocomplete';
import { BanDropdown } from './BanDropdown';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Input } from './Input';
import { Plus, Search, ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';
import {
  venueTypes,
  statusOptions,
  interestLevelOptions,
  musicStyles,
  MONTHS,
  emptyMonthPeriod,
  buildInterestsForForm,
  type VenueFormData,
  COORD_INVALIDATING_FIELDS,
} from './venueConstants';
import type { Venue, Address, Coordinates, MonthPeriod, Month, BandInterest, Band } from '../../types';
import { cleanAddress, emptyAddress } from '../../utils/addressFormat';
import { CountrySelect } from './CountrySelect';

// Column keys that can be edited
const EDITABLE_FIELDS: Record<string, string> = {
  type: 'Type',
  styles: 'Styles',
  url: 'URL',
  name: 'Name & Status',
  commentary: 'Commentary',
  address: 'Address',
  concert_period: 'Concert Period',
  interests: 'Interest Levels',
  ideal_contact_period: 'Contact Period',
  expect_answer_by: 'Expect Answer By',
};

interface VenueFieldEditorProps {
  venue: Venue;
  field: string;
  position: { x: number; y: number };
  onSave: (venueId: string, data: Partial<VenueFormData>) => void;
  onClose: () => void;
  readonlyCoreFields?: boolean;
  bands: Band[];
  /**
   * Stacking level of the portaled panel. The default clears a plain page; opened from inside a
   * modal it must clear that modal's backdrop (`.modal-backdrop` is at 9999).
   */
  zIndex?: number;
}

export function VenueFieldEditor({
  venue,
  field,
  position,
  onSave,
  onClose,
  readonlyCoreFields = false,
  bands,
  zIndex = 50,
}: VenueFieldEditorProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');

  // Field-specific state
  const [name, setName] = useState(venue.name || '');
  const [type, setType] = useState(venue.type);
  const [status, setStatus] = useState(venue.status || 'open');
  const [styles, setStyles] = useState<string[]>(venue.styles || []);
  const [newStyle, setNewStyle] = useState('');
  const [url, setUrl] = useState(venue.url || '');
  const [commentary, setCommentary] = useState(venue.commentary || '');
  const [address, setAddress] = useState<Address>(venue.address || emptyAddress());
  const [coordinates, setCoordinates] = useState<Coordinates | null>(venue.coordinates ?? null);
  const [banHighlightedIdx, setBanHighlightedIdx] = useState(-1);

  const { suggestions: banSuggestions, activeField: banActiveField, isLoading: banLoading, triggerSearch, selectSuggestion: selectBanSuggestion, dismiss: dismissBan } =
    useBanAutocomplete((updates, coords) => {
      setAddress(prev => ({ ...prev, ...updates }));
      setCoordinates(coords);
    });

  useEffect(() => { setBanHighlightedIdx(-1); }, [banSuggestions]);

  const BAN_TRIGGER_FIELDS = ['city', 'zip_code', 'administrative_region_1', 'administrative_region_2'] as const;

  const handleAddressFieldChange = (addressField: keyof Address, value: string) => {
    updateAddressField(addressField, value);
    if ((BAN_TRIGGER_FIELDS as readonly string[]).includes(addressField)) {
      // The BAN only knows French addresses — outside France there is nothing to suggest.
      triggerSearch(addressField as typeof BAN_TRIGGER_FIELDS[number], value, address.country);
    } else {
      dismissBan();
    }
  };

  const [concertPeriod, setConcertPeriod] = useState<MonthPeriod>(venue.concert_period || emptyMonthPeriod());
  const [idealContactPeriod, setIdealContactPeriod] = useState<MonthPeriod>(venue.ideal_contact_period || emptyMonthPeriod());
  const [expectAnswerBy, setExpectAnswerBy] = useState<MonthPeriod>(venue.expect_answer_by || emptyMonthPeriod());
  const [interests, setInterests] = useState<BandInterest[]>(buildInterestsForForm(venue.interests || [], bands));

  // Type dropdown
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Position the panel within viewport
  const [adjustedPos, setAdjustedPos] = useState(position);

  useEffect(() => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      let { x, y } = position;
      if (x + rect.width > window.innerWidth - 16) {
        x = window.innerWidth - rect.width - 16;
      }
      if (y + rect.height > window.innerHeight - 16) {
        y = window.innerHeight - rect.height - 16;
      }
      if (x < 16) x = 16;
      if (y < 16) y = 16;
      setAdjustedPos({ x, y });
    }
  }, [position]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Close type dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
        setTypeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fieldLabel = EDITABLE_FIELDS[field];
  if (!fieldLabel) return null;

  // Fields stored on the shared venue record (CORE_FIELDS in app/models/venue.py). The server
  // rejects a non-admin edit of any of them on a public venue, so the panel says so rather than
  // letting the save come back as an error. Everything else lives in the user's own venue_notes.
  const isCoreField = ['type', 'name', 'address', 'concert_period', 'styles', 'url'].includes(field);
  if (readonlyCoreFields && isCoreField) {
    return createPortal(
      <div
        ref={panelRef}
        className="fixed bg-dark-elevated border border-dark-border rounded-lg shadow-xl p-4 min-w-[240px]"
        style={{ left: adjustedPos.x, top: adjustedPos.y, zIndex }}
      >
        <div className="text-sm text-blue-400">
          This field is managed by the public venue and cannot be edited.
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>,
      document.body
    );
  }

  const handleSave = () => {
    setError('');
    let data: Partial<VenueFormData> = {};

    switch (field) {
      case 'name':
        if (!name.trim()) { setError('Name is required'); return; }
        data = { name: name.trim(), status };
        break;
      case 'url':
        data = { url: url.trim() };
        break;
      case 'commentary':
        data = { commentary: commentary.trim() };
        break;
      case 'type':
        data = { type };
        break;
      case 'styles':
        data = { styles };
        break;
      case 'address':
        data = { address: cleanAddress(address), coordinates };
        break;
      case 'concert_period':
        data = { concert_period: concertPeriod };
        break;
      case 'ideal_contact_period':
        data = { ideal_contact_period: idealContactPeriod };
        break;
      case 'expect_answer_by':
        data = { expect_answer_by: expectAnswerBy };
        break;
      case 'interests': {
        const cleanedInterests = interests.filter(
          i => i.level !== 'no_information' || (i.comment && i.comment.trim())
        );
        data = { interests: cleanedInterests };
        break;
      }
    }

    onSave(venue._id, data);
    onClose();
  };

  const handleAddStyle = (style: string) => {
    if (style && !styles.includes(style)) {
      setStyles([...styles, style]);
      setNewStyle('');
    }
  };

  const handleRemoveStyle = (style: string) => {
    setStyles(styles.filter(s => s !== style));
  };

  const updateAddressField = (addressField: keyof Address, value: string) => {
    setAddress(prev => ({ ...prev, [addressField]: value || undefined }));
    // Typing a location by hand (rather than picking a BAN suggestion) invalidates any stored
    // pin — coordinates are only ever re-established by a suggestion or the overlay backfill.
    if (COORD_INVALIDATING_FIELDS.has(addressField)) setCoordinates(null);
  };

  const toggleMonth = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void, month: Month) => {
    const months = period.months.includes(month)
      ? period.months.filter(m => m !== month)
      : [...period.months, month];
    setPeriod({ months, all_year: false });
  };

  const toggleAllYear = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void) => {
    setPeriod({ all_year: !period.all_year, months: [] });
  };

  const renderMonthPeriodEditor = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void) => (
    <div className="flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => toggleAllYear(period, setPeriod)}
        className={clsx(
          'px-2 py-0.5 rounded-sm text-xs font-medium border',
          period.all_year
            ? 'border-primary-600 bg-primary-900/30 text-primary-400'
            : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
        )}
      >
        All
      </button>
      {MONTHS.map(({ value, short }) => (
        <button
          key={value}
          type="button"
          onClick={() => toggleMonth(period, setPeriod, value)}
          disabled={period.all_year}
          className={clsx(
            'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
            period.all_year
              ? 'border-dark-border bg-dark-bg text-dark-text-muted cursor-not-allowed'
              : period.months.includes(value)
              ? 'border-primary-600 bg-primary-900/30 text-primary-400'
              : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
          )}
        >
          {short}
        </button>
      ))}
    </div>
  );

  const renderFieldEditor = () => {
    switch (field) {
      case 'name':
        return (
          <div className="space-y-3">
            <Input
              label="Venue Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as VenueFormData['status'])}
                className="input text-sm"
              >
                {statusOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'url':
        return (
          <div>
            <Input
              label="URL"
              type="text"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
        );

      case 'commentary':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Commentary</label>
            <textarea
              value={commentary}
              onChange={(e) => setCommentary(e.target.value)}
              rows={3}
              autoFocus
              className="input text-sm w-full resize-y"
              placeholder="Add a note about this venue..."
            />
          </div>
        );

      case 'type':
        return (
          <div ref={typeDropdownRef} className="relative">
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Type</label>
            <button
              type="button"
              onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setTypeSearch(''); }}
              className="w-full flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm font-medium text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            >
              <span>{venueTypes.find(t => t.value === type)?.label || 'Select type'}</span>
              <ChevronDown className={clsx('w-4 h-4 ml-2 text-dark-text-secondary transition-transform', typeDropdownOpen && 'rotate-180')} />
            </button>
            {typeDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-dark-border">
                  <Search className="w-3.5 h-3.5 text-dark-text-muted" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {venueTypes
                    .filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase()))
                    .map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setType(value); setTypeDropdownOpen(false); setTypeSearch(''); }}
                        className={clsx(
                          'w-full text-left px-3 py-2 text-sm',
                          type === value
                            ? 'bg-primary-900/30 text-primary-400'
                            : 'text-dark-text-secondary hover:bg-dark-border'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'styles':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Music Styles</label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                placeholder="Custom style..."
                value={newStyle}
                onChange={(e) => setNewStyle(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => handleAddStyle(newStyle)}
                disabled={!newStyle.trim()}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            {styles.filter(s => !musicStyles.includes(s)).length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {styles.filter(s => !musicStyles.includes(s)).map((style) => (
                  <span
                    key={style}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium"
                  >
                    {style}
                    <button type="button" onClick={() => handleRemoveStyle(style)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {musicStyles.map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => styles.includes(style) ? handleRemoveStyle(style) : handleAddStyle(style)}
                  className={clsx(
                    'px-2.5 py-1 rounded-full text-xs font-medium',
                    styles.includes(style)
                      ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                      : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                  )}
                >
                  {styles.includes(style) ? '✓ ' : '+ '}
                  {style}
                </button>
              ))}
            </div>
          </div>
        );

      case 'address':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Address</label>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Street" placeholder="123 Main St" value={address.street || ''} onChange={(e) => updateAddressField('street', e.target.value)} />
              <div className="relative">
                <Input label="City" placeholder="Paris" value={address.city || ''}
                  onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'city') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectBanSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'city' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectBanSuggestion} />
                )}
              </div>
              <div className="relative">
                <Input label="Postal Code" placeholder="75001" value={address.zip_code || ''}
                  onChange={(e) => handleAddressFieldChange('zip_code', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'zip_code') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectBanSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'zip_code' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectBanSuggestion} />
                )}
              </div>
              <div className="relative">
                <Input label="Dept/County" placeholder="Seine" value={address.administrative_region_1 || ''}
                  onChange={(e) => handleAddressFieldChange('administrative_region_1', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'administrative_region_1') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectBanSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'administrative_region_1' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectBanSuggestion} />
                )}
              </div>
              <div className="relative">
                <Input label="Region/State" placeholder="Ile-de-France" value={address.administrative_region_2 || ''}
                  onChange={(e) => handleAddressFieldChange('administrative_region_2', e.target.value)}
                  onBlur={() => setTimeout(dismissBan, 150)}
                  onKeyDown={(e) => {
                    if (!banSuggestions.length || banActiveField !== 'administrative_region_2') return;
                    if (e.key === 'ArrowDown') { e.preventDefault(); setBanHighlightedIdx(i => Math.min(i + 1, banSuggestions.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setBanHighlightedIdx(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && banHighlightedIdx >= 0) { e.preventDefault(); selectBanSuggestion(banSuggestions[banHighlightedIdx]); }
                    else if (e.key === 'Escape') dismissBan();
                  }}
                />
                {banActiveField === 'administrative_region_2' && (banSuggestions.length > 0 || banLoading) && (
                  <BanDropdown suggestions={banSuggestions} highlightedIdx={banHighlightedIdx} isLoading={banLoading} onSelect={selectBanSuggestion} />
                )}
              </div>
              <CountrySelect label="Country" value={address.country || ''} onChange={(val) => handleAddressFieldChange('country', val)} />
            </div>
          </div>
        );

      case 'concert_period':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Concert Period</label>
            {renderMonthPeriodEditor(concertPeriod, setConcertPeriod)}
          </div>
        );

      case 'ideal_contact_period':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Ideal Contact Period</label>
            {renderMonthPeriodEditor(idealContactPeriod, setIdealContactPeriod)}
          </div>
        );

      case 'expect_answer_by':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Expect Answer By</label>
            {renderMonthPeriodEditor(expectAnswerBy, setExpectAnswerBy)}
          </div>
        );

      case 'interests':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Interest Levels</label>
            <div className="space-y-2">
              {interests.map((interest, idx) => {
                const bandLabel = interest.band_id === null
                  ? 'Overall'
                  : bands.find(b => b._id === interest.band_id)?.name || interest.band_id;
                return (
                  <div key={interest.band_id ?? 'overall'} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-dark-text-primary min-w-[70px] shrink-0">
                      {bandLabel}
                    </span>
                    <div className="flex gap-1">
                      {interestLevelOptions.map(({ value, short }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            const updated = [...interests];
                            updated[idx] = { ...updated[idx], level: value };
                            setInterests(updated);
                          }}
                          className={clsx(
                            'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
                            interest.level === value
                              ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                              : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                          )}
                        >
                          {short}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Comment..."
                      value={interest.comment || ''}
                      onChange={(e) => {
                        const updated = [...interests];
                        updated[idx] = { ...updated[idx], comment: e.target.value || undefined };
                        setInterests(updated);
                      }}
                      className="flex-1 px-2 py-0.5 bg-dark-elevated border border-dark-border rounded-sm text-xs text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      className="fixed bg-dark-elevated border border-dark-border rounded-lg shadow-xl p-4 min-w-[280px] max-w-[480px]"
      style={{ left: adjustedPos.x, top: adjustedPos.y, zIndex }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-dark-text-primary">
          Edit {fieldLabel}
        </h3>
        <button
          onClick={onClose}
          className="p-0.5 hover:bg-dark-border rounded-sm text-dark-text-muted hover:text-dark-text-primary"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-900/20 border border-red-700/50 rounded-sm text-red-400 text-xs">
          {error}
        </div>
      )}

      {renderFieldEditor()}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave}>
          Update
        </Button>
      </div>
    </div>,
    document.body
  );
}
